import React, {useCallback} from "react";
import {useTranslation} from "react-i18next";
import {Badge, Box} from "@mui/material";
import StringInputField from "@/lib/components/inputfields/string-input-field";
import {ButtonEx} from "@/lib/components/buttons/ButtonEx";
import {OverviewTableI18N} from "@/translations";

export interface OverviewFilterBarProps {
    searchText: string;
    onSearchTextChange: (text: string) => void;
    /** The variant's free-text length limit (`REQUEST_SEARCH_TEXT_LENGTH_MAX` / `ORDER_SEARCH_TEXT_LENGTH_MAX`). */
    searchTextMaxLength: number;
    /**
     * The number of business field groups deviating from the defaults — computed by the variant-specific
     * table component via `countDeviatingFilterGroups`, never here (see the plan's "Standing constraints").
     * Zero hides the badge. The badge sits on "Filter zurücksetzen" (below) — the `.MuiBadge-badge` class
     * stays the specs' anchor.
     */
    badgeCount: number;
    /** "Filter zurücksetzen" — the caller wires this to `resetAll`. */
    onReset: () => void;
}

/**
 * Filter bar shared by every overview table variant (#1607): a free-text search on the left, and on the
 * right "Filter zurücksetzen" carrying a badge of the current filter-deviation count. Every filter VALUE
 * lives in the caller's `useOverviewTableState` applied state, this component never holds filter data of
 * its own (Phase 2.2). The former Sheet and its "Spaltenoptionen" opener are gone since Phase 3.3: every
 * column filters through its own header dropdown (`DataTableColumnHeader.tsx`) now, there is no second
 * Bedienweg left.
 *
 * Carries no sorting control — the sortable column header is the only trigger for a sort change (see the
 * plan's "Standing constraints").
 */
export const OverviewFilterBar: React.FC<OverviewFilterBarProps> = ({
    searchText,
    onSearchTextChange,
    searchTextMaxLength,
    badgeCount,
    onReset
}) => {
    const {t} = useTranslation();

    const handleSearchTextChange = useCallback(
        (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => onSearchTextChange(event.target.value),
        [onSearchTextChange]
    );

    return (
        <Box
            sx={{
                display: "flex",
                flexWrap: {xs: "wrap", sm: "nowrap"},
                alignItems: "flex-start",
                gap: 2
            }}
        >
            <Box sx={{flex: 1, minWidth: {xs: "100%", sm: "auto"}}}>
                <StringInputField
                    label={t(OverviewTableI18N.field.search)}
                    value={searchText}
                    onChange={handleSearchTextChange}
                    maxChars={searchTextMaxLength}
                />
            </Box>
            {/*
              * The badge sticks out of the button's box by half its own width. Every consumer renders the
              * filter bar inside a clipping container (`overflow: hidden` in `SectionedTable.tsx`,
              * `overflow: auto` in the non-fillHeight branch of `RequestOverviewTable.tsx`), so that
              * overhang gets cut off at the right edge. The margin reserves the space inside the container
              * instead of moving the badge, so the `.MuiBadge-badge` anchor the E2E specs use stays put.
              */}
            <Badge badgeContent={badgeCount} color="primary" invisible={badgeCount === 0} sx={{marginRight: "12px"}}>
                <ButtonEx label={t(OverviewTableI18N.action.reset)} onClick={onReset} variant="outlined" />
            </Badge>
        </Box>
    );
};
